import { useState, useEffect } from 'react';
import {
  Activity, AlertTriangle, ThermometerSun, Wind, Flower2, Cloud, CloudRain,
  Sun, ShieldAlert, Sparkles, RefreshCw, TrendingUp, TrendingDown, Minus,
  ChevronRight, Pill, MapPin, Info,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY = {
  high:   { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    chip: 'bg-red-600 text-white',    label: 'Haute' },
  medium: { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  chip: 'bg-amber-500 text-white',  label: 'Moyenne' },
  low:    { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   chip: 'bg-blue-500 text-white',   label: 'Faible' },
};

const LEVEL_COLOR = {
  epidemic: '#DC2626',
  alert:    '#F59E0B',
  watch:    '#3B82F6',
  low:      '#10B981',
};

const POLLEN_COLOR = {
  very_low: '#94A3B8', low: '#22C55E', medium: '#F59E0B', high: '#DC2626',
};

const AQI_COLOR = {
  very_good: '#10B981', good: '#84CC16', medium: '#F59E0B',
  poor: '#F97316', very_poor: '#DC2626',
};

const CATEGORY_ICON = {
  epidemie:  Activity,
  pollens:   Flower2,
  meteo:     ThermometerSun,
  pollution: Wind,
  tendance:  TrendingUp,
};

function fmtWeek(week) {
  if (!week) return '';
  const s = String(week);
  if (s.length < 6) return s;
  return `S${s.slice(4)} ${s.slice(0, 4)}`;
}

function TrendIcon({ pct }) {
  if (pct > 5) return <TrendingUp size={14} className="text-red-500" />;
  if (pct < -5) return <TrendingDown size={14} className="text-green-500" />;
  return <Minus size={14} className="text-gray-400" />;
}

function IndicatorCard({ ind, scope }) {
  if (!ind?.available) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="text-xs text-gray-400">{scope} : donn&eacute;es non disponibles</div>
      </div>
    );
  }
  const color = LEVEL_COLOR[ind.level] || LEVEL_COLOR.low;
  const max = Math.max(...ind.history_12w.map(h => h.inc100), ind.thresholds.epidemic);
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`indicator-${scope.toLowerCase()}-${ind.short.toLowerCase().replace(/[^a-z]/g,'')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{scope}</span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
          {ind.level === 'epidemic' ? 'Épidémie' : ind.level === 'alert' ? 'Alerte' : ind.level === 'watch' ? 'Vigilance' : 'Calme'}
        </span>
      </div>
      <h3 className="text-sm font-bold text-[#1B2A4A]">{ind.label}</h3>
      <div className="flex items-end gap-2 mt-2">
        <div className="text-3xl font-black text-[#1B2A4A]">{ind.current_inc100}</div>
        <div className="text-[10px] text-gray-400 mb-1.5">cas / 100k hab.</div>
      </div>
      <div className="flex items-center gap-1 mt-1">
        <TrendIcon pct={ind.trend_pct_4w} />
        <span className={`text-xs font-semibold ${ind.trend_pct_4w > 0 ? 'text-red-500' : ind.trend_pct_4w < 0 ? 'text-green-500' : 'text-gray-400'}`}>
          {ind.trend_pct_4w > 0 ? '+' : ''}{ind.trend_pct_4w}% sur 4 semaines
        </span>
      </div>

      {/* Sparkline 12w */}
      <div className="mt-3 flex items-end gap-0.5 h-12">
        {ind.history_12w.map((h, i) => {
          const pct = max ? (h.inc100 / max) * 100 : 0;
          const c = h.inc100 >= ind.thresholds.epidemic ? '#DC2626'
            : h.inc100 >= ind.thresholds.alert ? '#F59E0B'
            : h.inc100 >= ind.thresholds.alert / 2 ? '#3B82F6' : '#10B981';
          return (
            <div key={i} className="flex-1 rounded-t" style={{ height: `${Math.max(pct, 4)}%`, backgroundColor: c, opacity: i === 11 ? 1 : 0.55 }} title={`${fmtWeek(h.week)}: ${h.inc100}`} />
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-gray-400">
        <span>{ind.history_12w.length ? fmtWeek(ind.history_12w[0].week) : ''}</span>
        <span>{ind.history_12w.length ? fmtWeek(ind.history_12w[ind.history_12w.length - 1].week) : ''}</span>
      </div>

      {/* Forecast */}
      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
        <span className="text-[10px] text-gray-400 font-semibold uppercase">Projection 14 j</span>
        <span className="text-sm font-bold text-[#1B2A4A]">≈ {ind.forecast_inc100_2w} / 100k</span>
      </div>
    </div>
  );
}

function AlertCard({ alert }) {
  const s = SEVERITY[alert.severity] || SEVERITY.low;
  const Icon = CATEGORY_ICON[alert.category] || ShieldAlert;
  return (
    <div className={`${s.bg} ${s.border} border rounded-xl p-4`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${s.chip} flex items-center justify-center shrink-0`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${s.chip}`}>{s.label}</span>
            <h4 className={`text-sm font-bold ${s.text}`}>{alert.title}</h4>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{alert.message}</p>
          {alert.products?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {alert.products.slice(0, 6).map(p => (
                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-white text-gray-600">{p}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PollenBlock({ pollens }) {
  if (!pollens?.available) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="pollens-block">
      <div className="flex items-center gap-2 mb-3">
        <Flower2 size={14} className="text-pink-500" />
        <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider">Pollens (CAMS)</h3>
      </div>
      <div className="space-y-2">
        {pollens.items.map(p => (
          <div key={p.key} className="flex items-center gap-2">
            <span className="w-20 text-[11px] text-gray-500">{p.label}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-2 rounded-full" style={{
                width: `${Math.min(100, (p.value / 100) * 100)}%`,
                backgroundColor: POLLEN_COLOR[p.level],
              }} />
            </div>
            <span className="text-[10px] text-gray-400 w-12 text-right">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ENV_KEY_META = {
  allergies:       { label: 'Allergies pollins\u00e9es', icon: Flower2, color: '#EC4899' },
  bronchiolite:    { label: 'Bronchiolite',         icon: CloudRain,    color: '#2563EB' },
  conjonctivite:   { label: 'Conjonctivites',       icon: Activity,     color: '#0EA5E9' },
  uti:             { label: 'Cystites',             icon: Activity,     color: '#F97316' },
  dermato_chaleur: { label: 'UV / Chaleur',         icon: Sun,          color: '#F59E0B' },
};

function EnvIndicatorCard({ envKey, env }) {
  const meta = ENV_KEY_META[envKey] || { label: envKey, icon: Activity, color: '#888' };
  const Icon = meta.icon;
  const levelColor = env.level === 'alert' ? '#DC2626' : env.level === 'watch' ? '#F59E0B' : '#10B981';
  const levelLabel = env.level === 'alert' ? 'Alerte' : env.level === 'watch' ? 'Vigilance' : 'Calme';
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`env-card-${envKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}15` }}>
          <Icon size={13} style={{ color: meta.color }} />
        </div>
        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: levelColor }}>
          {levelLabel}
        </span>
      </div>
      <h4 className="text-xs font-bold text-[#1B2A4A]">{env.label || meta.label}</h4>
      <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{env.headline}</p>
    </div>
  );
}

function WeatherBlock({ weather }) {
  if (!weather?.available) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="weather-block">
      <div className="flex items-center gap-2 mb-3">
        <ThermometerSun size={14} className="text-orange-500" />
        <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider">M&eacute;t&eacute;o 7 jours</h3>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div><p className="text-2xl font-black text-[#1B2A4A]">{weather.current_temp}°</p><p className="text-[10px] text-gray-400">Actuel</p></div>
        <div><p className="text-2xl font-black text-[#1B2A4A]">{weather.max_uv_7d}</p><p className="text-[10px] text-gray-400">UV max</p></div>
        <div><p className="text-2xl font-black text-[#1B2A4A]">{weather.current_humidity}%</p><p className="text-[10px] text-gray-400">Humidit&eacute;</p></div>
      </div>
      <div className="flex gap-1">
        {weather.forecast?.map((d, i) => (
          <div key={i} className="flex-1 text-center bg-gray-50 rounded p-1">
            <p className="text-[9px] text-gray-400">{d.date?.slice(8, 10)}/{d.date?.slice(5, 7)}</p>
            <p className="text-[11px] font-bold text-[#1B2A4A]">{Math.round(d.tmax)}°</p>
            <p className="text-[9px] text-gray-400">{Math.round(d.tmin)}°</p>
            {d.uv >= 7 && <Sun size={10} className="text-orange-500 mx-auto" />}
            {d.rain > 1 && <CloudRain size={10} className="text-blue-500 mx-auto" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function AirQualityBlock({ air }) {
  if (!air?.available) return null;
  const c = AQI_COLOR[air.level] || '#888';
  const labels = { very_good: 'Tr&egrave;s bonne', good: 'Bonne', medium: 'Moyenne', poor: 'Mauvaise', very_poor: 'Tr&egrave;s mauvaise' };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="air-quality-block">
      <div className="flex items-center gap-2 mb-3">
        <Wind size={14} className="text-cyan-500" />
        <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider">Qualit&eacute; de l'air</h3>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: `${c}22` }}>
          <span className="text-2xl font-black" style={{ color: c }}>{air.aqi}</span>
        </div>
        <div>
          <p className="text-sm font-bold" style={{ color: c }} dangerouslySetInnerHTML={{ __html: labels[air.level] }} />
          <p className="text-[10px] text-gray-400">PM10 {air.pm10} &middot; PM2.5 {air.pm25}</p>
        </div>
      </div>
    </div>
  );
}

function StockTable({ section, selected, onToggle, onQtyChange }) {
  const allRowIds = section.rows.map(r => `${section.indicator_key}::${r.name}`);
  const allSelected = allRowIds.every(id => selected.has(id));
  const someSelected = allRowIds.some(id => selected.has(id));

  const toggleAll = () => {
    onToggle(allRowIds, !allSelected);
  };

  const levelColors = {
    epidemic: '#DC2626',
    alert: '#F59E0B',
    watch: '#3B82F6',
    low: '#10B981',
  };
  const levelLabels = {
    epidemic: "\u00c9pid\u00e9mie",
    alert: "Alerte",
    watch: "Vigilance",
    low: "Calme",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`stock-section-${section.indicator_key}`}>
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase text-white" style={{ backgroundColor: levelColors[section.level] || '#888' }}>
            {levelLabels[section.level] || section.level}
          </span>
          <h3 className="text-sm font-bold text-[#1B2A4A]">{section.indicator_label}</h3>
        </div>
        {section.headline && (
          <p className="text-[11px] text-gray-500">{section.headline}</p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-2 py-2 text-left">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                       data-testid={`select-all-${section.indicator_key}`}
                       className="cursor-pointer accent-[#1B2A4A]" />
              </th>
              <th className="px-2 py-2 text-left font-bold text-gray-500 uppercase">Produit</th>
              <th className="px-2 py-2 text-left font-bold text-gray-500 uppercase">Dosage</th>
              <th className="px-2 py-2 text-left font-bold text-gray-500 uppercase">CIP13</th>
              <th className="px-2 py-2 text-left font-bold text-gray-500 uppercase">ATC</th>
              <th className="px-2 py-2 text-left font-bold text-gray-500 uppercase">Labo</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">Prix TTC</th>
              <th className="px-2 py-2 text-center font-bold text-gray-500 uppercase">Remb.</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">Rotation</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">Surstock</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">Qt&eacute;</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">D&eacute;lai</th>
              <th className="px-2 py-2 text-right font-bold text-gray-500 uppercase">Engagement</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map(r => {
              const id = `${section.indicator_key}::${r.name}`;
              const sel = selected.has(id);
              const qty = sel ? (selected.get(id)?.qty ?? r.qty_suggeree) : r.qty_suggeree;
              const remColor = r.taux_remb >= 65 ? '#059669' : r.taux_remb >= 30 ? '#F59E0B' : '#94A3B8';
              return (
                <tr key={id} className={`border-b border-gray-50 ${sel ? 'bg-[#B8860B]/5' : 'hover:bg-gray-50'}`} data-testid={`stock-row-${id}`}>
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={sel} onChange={() => onToggle([id], !sel)}
                           className="cursor-pointer accent-[#1B2A4A]"
                           data-testid={`select-${id}`} />
                  </td>
                  <td className="px-2 py-2 font-semibold text-[#1B2A4A] whitespace-nowrap max-w-[250px] truncate" title={r.name}>{r.name}</td>
                  <td className="px-2 py-2 text-gray-600 whitespace-nowrap"><div>{r.dosage}</div><div className="text-[9px] text-gray-400">{r.conditionnement}</div></td>
                  <td className="px-2 py-2 text-gray-500 font-mono text-[10px]">{r.cip13 || '-'}</td>
                  <td className="px-2 py-2 text-gray-500"><div className="font-mono text-[10px]">{r.atc || '-'}</div><div className="text-[9px] text-gray-400">{r.atc_label}</div></td>
                  <td className="px-2 py-2 text-gray-600">{r.labo}</td>
                  <td className="px-2 py-2 text-right font-bold text-[#1B2A4A]">{(r.prix_ttc || 0).toFixed(2)}{'\u20ac'}</td>
                  <td className="px-2 py-2 text-center">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: remColor }}>
                      {r.taux_remb}%
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right text-gray-500">{r.rotation_mensuelle}/m</td>
                  <td className="px-2 py-2 text-right">
                    <span className="font-bold" style={{ color: r.surstock_pct >= 50 ? '#DC2626' : r.surstock_pct >= 30 ? '#F59E0B' : '#3B82F6' }}>
                      +{r.surstock_pct}%
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <input type="number" min={0} value={qty} disabled={!sel}
                           onChange={(e) => onQtyChange(id, parseInt(e.target.value || 0, 10))}
                           data-testid={`qty-${id}`}
                           className={`w-16 text-right text-xs border ${sel ? 'border-[#1B2A4A] bg-white' : 'border-transparent bg-transparent'} rounded px-1 py-0.5`} />
                  </td>
                  <td className="px-2 py-2 text-right text-gray-500">{r.delai_reappro_jours}j</td>
                  <td className="px-2 py-2 text-right font-bold text-[#1B2A4A]">{(qty * (r.prix_achat || 0)).toFixed(2)}{'\u20ac'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockSummaryFooter({ selectedRows, onExportCsv, onExportPdf }) {
  const refs = selectedRows.length;
  const totalQty = selectedRows.reduce((s, r) => s + r.qty, 0);
  const totalEng = selectedRows.reduce((s, r) => s + r.qty * (r.prix_achat || 0), 0);
  const totalMarge = selectedRows.reduce((s, r) => s + r.qty * ((r.prix_ttc || 0) - (r.prix_achat || 0)), 0);
  return (
    <div className="sticky bottom-0 bg-[#1B2A4A] text-white rounded-xl p-4 shadow-2xl mt-4" data-testid="stock-summary-footer">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-white/60">Refs</p>
          <p className="text-2xl font-black">{refs}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-white/60">Quantit&eacute; totale</p>
          <p className="text-2xl font-black">{totalQty}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-white/60">Engagement</p>
          <p className="text-2xl font-black text-[#B8860B]">{totalEng.toFixed(2)}{'\u20ac'}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-white/60">Marge potentielle</p>
          <p className="text-2xl font-black text-[#10B981]">{totalMarge.toFixed(2)}{'\u20ac'}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={onExportCsv} disabled={refs === 0}
                data-testid="export-csv-grossiste-btn"
                className="flex-1 py-2 px-3 rounded-lg bg-white text-[#1B2A4A] text-xs font-bold hover:bg-gray-100 transition disabled:opacity-50">
          Export CSV grossiste (CIP13/qty)
        </button>
        <button onClick={onExportPdf} disabled={refs === 0}
                data-testid="export-pdf-btn"
                className="flex-1 py-2 px-3 rounded-lg bg-[#B8860B] text-white text-xs font-bold hover:bg-[#9C710A] transition disabled:opacity-50">
          Export PDF imprimable (A4)
        </button>
      </div>
    </div>
  );
}


function DigestBlock({ digest, loading, onRefresh }) {
  return (
    <div className="bg-gradient-to-br from-[#1B2A4A] to-[#2C3E5C] rounded-xl p-5 text-white relative overflow-hidden" data-testid="ai-digest">
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-20 translate-x-20" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#B8860B]" />
            <h3 className="text-sm font-bold uppercase tracking-wider">Digest IA hebdomadaire</h3>
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
            data-testid="digest-refresh-btn"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> {loading ? 'En cours' : 'Actualiser'}
          </button>
        </div>
        {!digest && !loading && (
          <p className="text-xs text-white/60">Cliquez sur Actualiser pour g&eacute;n&eacute;rer le premier digest.</p>
        )}
        {loading && !digest && (
          <p className="text-xs text-white/60">Génération en cours...</p>
        )}
        {digest && (
          <div className="text-xs leading-relaxed text-white/90 whitespace-pre-line" data-testid="digest-content">
            {digest.digest}
          </div>
        )}
        {digest?.generated_at && (
          <p className="text-[9px] text-white/40 mt-3 uppercase">G&eacute;n&eacute;r&eacute; le {new Date(digest.generated_at).toLocaleDateString('fr-FR')}</p>
        )}
      </div>
    </div>
  );
}

export default function RadarTab() {
  const [data, setData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [selectedRows, setSelectedRows] = useState(new Map());

  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/radar/dashboard`, { headers });
      const json = await res.json();
      setData(json);
      const dRes = await fetch(`${API_URL}/radar/digest`, { headers });
      if (dRes.ok) {
        const dJson = await dRes.json();
        setDigest(dJson);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadStockTable = async () => {
    setStockLoading(true);
    try {
      const res = await fetch(`${API_URL}/radar/stock-table`, { headers });
      if (res.ok) {
        const j = await res.json();
        setStockData(j);
      }
    } catch (e) { console.error(e); }
    setStockLoading(false);
  };

  const handleToggle = (ids, on) => {
    setSelectedRows(prev => {
      const next = new Map(prev);
      ids.forEach(id => {
        if (on) {
          if (!next.has(id)) {
            const [secKey, name] = id.split('::');
            const section = stockData?.sections?.find(s => s.indicator_key === secKey);
            const row = section?.rows?.find(r => r.name === name);
            if (row) next.set(id, { ...row, qty: row.qty_suggeree });
          }
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleQtyChange = (id, qty) => {
    setSelectedRows(prev => {
      const next = new Map(prev);
      const row = next.get(id);
      if (row) next.set(id, { ...row, qty: Math.max(0, qty || 0) });
      return next;
    });
  };

  const exportCsv = () => {
    const rows = [['CIP13', 'Designation', 'Quantite', 'Conditionnement']];
    [...selectedRows.values()].forEach(r => {
      rows.push([r.cip13 || '', r.name, r.qty, r.conditionnement || '']);
    });
    const csv = rows.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commande-grossiste-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const rows = [...selectedRows.values()];
    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const officineName = data?.city ? `Officine - ${data.city}` : 'Officine';
    const totalEng = rows.reduce((s, r) => s + r.qty * (r.prix_achat || 0), 0);
    const totalQty = rows.reduce((s, r) => s + r.qty, 0);
    const html = `
<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Bon de commande - Benchory</title>
<style>
  @page { size: A4 portrait; margin: 18mm; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1B2A4A; font-size: 11pt; }
  .head { border-bottom: 2px solid #1B2A4A; padding-bottom: 12px; margin-bottom: 18px; display:flex; justify-content:space-between; align-items:flex-start; }
  .brand { font-size: 22pt; font-weight: 900; color: #1B2A4A; letter-spacing: 1px; }
  .meta { text-align: right; font-size: 10pt; color: #555; }
  h1 { font-size: 14pt; margin: 16px 0 10px 0; }
  table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 9.5pt; }
  th { background: #1B2A4A; color: white; text-align: left; padding: 7px 6px; font-size: 9pt; }
  td { padding: 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  tr:nth-child(even) td { background: #FAFAFA; }
  .total { margin-top: 14px; padding: 10px; background: #FBF7EE; border: 1px solid #B8860B; border-radius: 6px; }
  .total b { color: #B8860B; font-size: 14pt; }
  .sign { margin-top: 32px; display:flex; justify-content:space-between; gap: 30px; font-size: 10pt; }
  .sign div { flex: 1; border-top: 1px dashed #999; padding-top: 4px; }
  .footer { margin-top: 30px; font-size: 8pt; color: #888; text-align:center; }
  .warn { background: #FEF3C7; border: 1px solid #F59E0B; padding: 6px 10px; margin: 10px 0; font-size: 9pt; border-radius: 4px; }
</style></head><body>
<div class="head">
  <div>
    <div class="brand">BENCHORY</div>
    <div style="color:#666; font-size:10pt; margin-top:4px;">${officineName}</div>
  </div>
  <div class="meta">
    Date: <b>${today}</b><br/>
    Bon n&deg;: BCH-${Date.now().toString().slice(-8)}<br/>
    G&eacute;n&eacute;r&eacute; depuis Benchory
  </div>
</div>

<h1>Bon de commande grossiste</h1>
<p style="color:#666; font-size:10pt;">Renforcement de stock recommand&eacute; sur la base des indicateurs sanitaires Benchory.</p>

${stockData?.is_estimated ? `<div class="warn">${stockData.estimation_warning}</div>` : ''}

<table>
  <thead><tr>
    <th style="width:14%">CIP13</th>
    <th style="width:34%">D&eacute;signation</th>
    <th style="width:14%">Conditionnement</th>
    <th style="width:8%; text-align:right">Qt&eacute;</th>
    <th style="width:10%; text-align:right">PU TTC</th>
    <th style="width:10%; text-align:right">PA estim&eacute;</th>
    <th style="width:10%; text-align:right">Total achat</th>
  </tr></thead>
  <tbody>
    ${rows.map(r => `<tr>
      <td style="font-family:monospace">${r.cip13 || ''}</td>
      <td><b>${r.name}</b><br/><span style="color:#888; font-size:8.5pt">${r.atc || ''} ${r.atc_label || ''}</span></td>
      <td>${r.conditionnement || ''}</td>
      <td style="text-align:right; font-weight:bold">${r.qty}</td>
      <td style="text-align:right">${(r.prix_ttc || 0).toFixed(2)}&euro;</td>
      <td style="text-align:right">${(r.prix_achat || 0).toFixed(2)}&euro;</td>
      <td style="text-align:right; font-weight:bold">${(r.qty * (r.prix_achat || 0)).toFixed(2)}&euro;</td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="total">
  <b>Total: ${totalQty} unit&eacute;s &middot; Engagement estim&eacute;: ${totalEng.toFixed(2)} &euro;</b>
</div>

<div class="sign">
  <div>Visa pharmacien titulaire</div>
  <div>Cachet officine</div>
</div>

<div class="footer">
  Document &eacute;dit&eacute; via Benchory &middot; Plateforme de pilotage officinal &middot; benchory.fr
</div>
</body></html>`;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    }
  };

  const refreshDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await fetch(`${API_URL}/radar/digest?refresh=true`, { headers });
      const json = await res.json();
      setDigest(json);
    } catch (e) {
      console.error(e);
    }
    setDigestLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === 'stock' && !stockData) loadStockTable();
    // eslint-disable-next-line
  }, [tab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="radar-loading">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
        <AlertTriangle size={36} className="text-amber-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">Donn&eacute;es indisponibles pour le moment.</p>
      </div>
    );
  }

  const epi = data.epidemiology_local || {};
  const epiNat = data.epidemiology_national || {};
  const alerts = data.alerts || [];
  const recos = data.recommendations || [];

  return (
    <div className="space-y-4" data-testid="radar-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-[#1B2A4A]" />
            <span className="text-xs font-bold text-[#1B2A4A]">
              {data.city || 'Votre zone'} · {data.region_label || data.region}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Donn&eacute;es publiques agr&eacute;g&eacute;es : R&eacute;seau Sentinelles (Sentiweb), Open-Meteo, CAMS Copernicus
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { k: 'overview', l: 'Synth\u00e8se' },
            { k: 'alerts', l: `Alertes (${alerts.length})` },
            { k: 'stock', l: `Stock (${recos.length})` },
            { k: 'digest', l: 'Digest IA' },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition ${tab === t.k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'}`}
              data-testid={`radar-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && (
        <>
          {/* Summary alert banner */}
          {alerts.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="alerts-banner">
              <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-700">{alerts.length} alerte(s) active(s) dans votre zone</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {alerts.slice(0, 3).map(a => a.title).join(' · ')}
                </p>
              </div>
              <button onClick={() => setTab('alerts')} className="ml-auto text-xs font-bold text-amber-700 underline shrink-0" data-testid="see-all-alerts-btn">
                Tout voir
              </button>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
              <Activity size={18} className="text-green-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-green-700">Situation sanitaire calme dans votre zone</p>
                <p className="text-xs text-green-600 mt-0.5">Aucune alerte significative d&eacute;tect&eacute;e cette semaine.</p>
              </div>
            </div>
          )}

          {/* Local indicators - Sentiweb pathologies */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {epi.ira && <IndicatorCard ind={epi.ira} scope={"R\u00e9gional"} />}
            {epi.gea && <IndicatorCard ind={epi.gea} scope={"R\u00e9gional"} />}
            {epi.varicelle && <IndicatorCard ind={epi.varicelle} scope={"R\u00e9gional"} />}
          </div>

          {/* Environmental indicators */}
          {data.env_indicators && Object.keys(data.env_indicators).length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Wind size={13} /> Indicateurs environnementaux
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(data.env_indicators).map(([key, e]) => (
                  <EnvIndicatorCard key={key} envKey={key} env={e} />
                ))}
              </div>
            </div>
          )}

          {/* Environment row (raw data) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PollenBlock pollens={data.pollens} />
            <WeatherBlock weather={data.weather} />
            <AirQualityBlock air={data.air_quality} />
          </div>

          {/* National vs local quick compare */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info size={13} className="text-gray-400" />
              <h3 className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider">Comparaison avec la France</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {['ira', 'gea', 'varicelle'].map(k => epi[k]?.available && epiNat[k]?.available && (
                <div key={k} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold">{epi[k].short}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{data.region_label || data.region}: <strong>{epi[k].current_inc100}</strong> / France: <strong>{epiNat[k].current_inc100}</strong></p>
                  </div>
                  <div className={`text-xs font-bold ${epi[k].current_inc100 > epiNat[k].current_inc100 ? 'text-red-500' : 'text-green-500'}`}>
                    {epi[k].current_inc100 > epiNat[k].current_inc100 ? '+' : ''}{epiNat[k].current_inc100 ? Math.round((epi[k].current_inc100 / epiNat[k].current_inc100 - 1) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'alerts' && (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <ShieldAlert size={36} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune alerte active dans votre zone</p>
              <p className="text-[10px] text-gray-400 mt-1">Le radar surveille en continu Sentinelles, m&eacute;t&eacute;o et CAMS.</p>
            </div>
          ) : alerts.map(a => <AlertCard key={a.id} alert={a} />)}
        </div>
      )}

      {tab === 'stock' && (
        <div data-testid="stock-tab" className="space-y-3">
          {stockLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
            </div>
          )}
          {!stockLoading && stockData?.sections?.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <Pill size={36} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune pathologie active n&eacute;cessitant un renfort stock</p>
              <p className="text-[10px] text-gray-400 mt-1">Le tableau de pilotage stock s'active des qu'un indicateur passe en alerte ou epidemie.</p>
            </div>
          )}
          {!stockLoading && stockData?.is_estimated && stockData.sections.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700 leading-relaxed">{stockData.estimation_warning}</p>
            </div>
          )}
          {stockData?.sections?.map(s => (
            <StockTable
              key={s.indicator_key}
              section={s}
              selected={selectedRows}
              onToggle={handleToggle}
              onQtyChange={handleQtyChange}
            />
          ))}
          {selectedRows.size > 0 && (
            <StockSummaryFooter
              selectedRows={[...selectedRows.values()]}
              onExportCsv={exportCsv}
              onExportPdf={exportPdf}
            />
          )}
        </div>
      )}

      {tab === 'digest' && (
        <DigestBlock digest={digest} loading={digestLoading} onRefresh={refreshDigest} />
      )}

      {/* Sources footer */}
      <div className="text-[10px] text-gray-400 text-center py-3">
        Sources: {data.sources?.map(s => s.label).join(' · ')} · G&eacute;n&eacute;r&eacute; {new Date(data.generated_at).toLocaleString('fr-FR')}
      </div>
    </div>
  );
}
